/**
 * ConciergeGrid Component
 * Main concierge area with 8 service cards in 4x2 grid
 * Header and zone indicator always visible; content area switches views
 * Phase D3.3: Global voice hints footer - always visible
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTVMotion } from '@/hooks/useTVMotion';
import {
  Mic, CloudSun, MessageSquare, CalendarDays, Compass, Wrench, Leaf,
  UtensilsCrossed, Utensils, Wine, Waves, Sparkles, Dumbbell, Car,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useKeyboardNavigation } from '@/hooks';
import { ModernServiceCard } from './ModernServiceCard';
import { ZoneIndicator } from './ZoneIndicator';
import { ServiceDetailPage } from './ServiceDetailPage';
import { StandardServiceDetailPage } from './StandardServiceDetailPage';
import { RoomServiceDetailPage } from './RoomServiceDetailPage';
import { SpaDetailPage } from './SpaDetailPage';
import { RestaurantDetailPage } from './RestaurantDetailPage';
import { WeatherPlaceholderView } from './WeatherPlaceholderView';
import { MessagesPlaceholderView } from './MessagesPlaceholderView';
import { StayTimelineCalendar } from './StayTimelineCalendar';
import { ExploreView } from './ExploreView';
import { MyRequestsView, type MyRequestsViewRef } from './MyRequestsView';
import { GreenStayView, type GreenStayVoiceCommand } from './GreenStayView';
import { mockServiceCards } from '@/data/mockHotelData';
import { ServiceCategory } from '@/types';
import type { NavigationZone, ServiceDetail, ServiceOption, Booking, CalendarEvent } from '@/types';
import type { CreateBookingRequest } from '@/api';

// Type for concierge panel view
// Phase D4.5.1: Removed 'askzaxia' - voice is now a global overlay, not a navigable view
// Phase P2.2-9: Added 'requests' for My Requests view
// Phase P2.2-10: Added 'green-stay' for Green Stay management view
export type ConciergeView = 'grid' | 'service' | 'weather' | 'messages' | 'calendar' | 'explore' | 'requests' | 'green-stay';

// TV-motion hook is consumed inside the component

// Phase D3.3: Voice hints type for global footer
export interface VoiceHintConfig {
  title: string;
  hints: string[];
  variant?: 'default' | 'action' | 'confirm';
}

// Default voice hints for grid view - always visible in concierge area
const DEFAULT_VOICE_HINTS: VoiceHintConfig = {
  title: 'Say something like',
  hints: ['Open calendar', 'Room service', 'Book a spa', 'Weather', 'TV ON'],
  variant: 'default',
};

// Module-level icon map — created once, reused every render
const SERVICE_ICON_MAP: Record<string, ReactNode> = {
  card_room_service: <UtensilsCrossed className="w-6 h-6 text-white/70" />,
  card_restaurant:   <Utensils        className="w-6 h-6 text-white/70" />,
  card_bar:          <Wine            className="w-6 h-6 text-white/70" />,
  card_pool:         <Waves           className="w-6 h-6 text-white/70" />,
  card_spa:          <Sparkles        className="w-6 h-6 text-white/70" />,
  card_gym:          <Dumbbell        className="w-6 h-6 text-white/70" />,
  card_explore:      <Compass         className="w-6 h-6 text-white/70" />,
  card_transport:    <Car             className="w-6 h-6 text-white/70" />,
};

interface ConciergeGridProps {
  enabled: boolean;
  onServiceSelect?: (serviceId: string) => void;
  activeZone: NavigationZone;
  // Phase 2B: Service detail props
  selectedService?: ServiceDetail | null;
  onServiceBack?: () => void;
  onOptionSelect?: (option: ServiceOption) => void;
  // Phase 2B: Placeholder views
  activeView?: ConciergeView;
  onViewClose?: () => void;
  // Phase 3A: Calendar props - now from backend
  bookings?: Booking[];
  calendarEvents?: CalendarEvent[];
  onSelectBooking?: (booking: Booking) => void;
  onSelectEvent?: (event: CalendarEvent) => void;
  // Phase 2C: Allow views to be active when opened from sidebar
  viewIsOpen?: boolean;

  // Phase D4.2: Scheduled Green Stay days for calendar display
  skipCleaningDays?: string[];
  towelsOnlyDays?: string[];
  // Phase P2.2-10: Green Stay controls
  greenStayEnabled?: boolean;
  onGreenStayEnable?: () => void;
  onGreenStayDisable?: () => void;
  onSkipCleaningChange?: (days: string[]) => void;
  onTowelsOnlyChange?: (days: string[]) => void;
  // Phase P2.2-10: Stay identifiers for eco logging
  roomNumber?: string;
  stayId?: string;
  // Phase D4.5.1: Booking created callback for sub-views (Room Service, Spa, Explore)
  onVoiceBookingCreated?: (booking: CreateBookingRequest) => Promise<void>;
  // Phase D4.5.2: Initial slots for restaurant booking from voice
  restaurantInitialSlots?: { time?: string; partySize?: number; transcript?: string };
  // P2.7-2: Pre-selected option ID for generic service detail pages (Bar/Pool/Gym/Transport)
  serviceInitialOption?: string;
  // Phase D4.5.2: Stay dates for restaurant date selection
  stayCheckIn?: string;
  stayCheckOut?: string;
  // Phase D4.5.3: Initial date for calendar (voice navigation)
  calendarInitialDate?: string;
  // Phase D4.5.3: Booking cancellation callback
  onBookingCancelled?: (bookingId: string) => void;
  // Phase D4.5.3: Voice-initiated cancellation
  pendingCancelBookingId?: string;
  onPendingCancelHandled?: () => void;
  // Voice-initiated selection
  pendingSelectBookingId?: string;
  onPendingSelectHandled?: () => void;
  // Phase D4.5.3: Voice confirmation of cancellation
  voiceConfirmCancel?: boolean;
  voiceKeepBooking?: boolean;
  onVoiceActionHandled?: () => void;
  /** Called when cancel-confirm dialog is dismissed */
  onCancelConfirmDismissed?: () => void;
  // Phase D4.5.4: Modify booking props
  pendingModifyBookingId?: string;
  pendingModifyChanges?: { newTime?: string; newDay?: string; newPartySize?: number };
  onPendingModifyHandled?: () => void;
  voiceConfirmModify?: boolean;
  voiceCancelModify?: boolean;
  onModifyVoiceActionHandled?: () => void;
  onModifyConfirmDismissed?: () => void;
  onBookingModified?: (bookingId: string) => void;
  // Voice booking list navigation
  voiceBookingNavTrigger?: 'next' | 'prev';
  onVoiceBookingNavHandled?: () => void;
  voiceDayNavTrigger?: 'next' | 'prev';
  onVoiceDayNavHandled?: () => void;
  // Phase P2.2-9: Callback to register the requests voice command handler
  onRequestsVoiceCommandReady?: (handler: (command: {
    type: 'NEXT_COLUMN' | 'PREV_COLUMN' | 'GO_TO_COLUMN' | 'NEXT_REQUEST' | 'PREV_REQUEST' |
          'FIRST_REQUEST' | 'LAST_REQUEST' | 'GO_TO_INDEX' | 'EXPAND' | 'COLLAPSE' |
          'READ_REQUEST' | 'COUNT' | 'STATUS' | 'BACK';
    payload?: { column?: 'NEW' | 'IN_PROGRESS' | 'COMPLETED'; index?: number };
  }) => string | void) => void;
  // Phase P2.2-10: Callback to register the green stay voice command handler
  onGreenStayVoiceCommandReady?: (handler: (command: GreenStayVoiceCommand, date?: string) => string) => void;
  /** P2.8-4b: D-Pad boundary: left from first card → sidebar zone */
  onGridLeftBoundary?: () => void;
  /** P2.8-4b: D-Pad boundary: right from last card → TV zone */
  onGridRightBoundary?: () => void;
  /** P2.9-1: Register Messages view synchronous voice command handler */
  onMessagesVoiceCommandReady?: (handler: (transcript: string) => string) => void;
}

export function ConciergeGrid({
  enabled,
  onServiceSelect,
  activeZone,
  selectedService,
  onServiceBack,
  onOptionSelect,
  activeView = 'grid',
  onViewClose,
  bookings,
  calendarEvents,
  onSelectBooking,
  onSelectEvent,
  viewIsOpen = false,
  // Phase D4.2: Scheduled preferences for calendar display
  skipCleaningDays,
  towelsOnlyDays,
  // Phase P2.2-10: Green Stay controls
  greenStayEnabled,
  onGreenStayEnable,
  onGreenStayDisable,
  onSkipCleaningChange,
  onTowelsOnlyChange,
  // Phase D4.5.1: Booking callback for sub-views
  onVoiceBookingCreated,
  // Phase D4.5.2: Initial slots for restaurant booking from voice
  restaurantInitialSlots,
  // P2.7-2: Pre-selected option for generic service detail pages
  serviceInitialOption,
  // Phase D4.5.2: Stay dates for restaurant date selection
  stayCheckIn,
  stayCheckOut,
  roomNumber,
  stayId,
  // Phase D4.5.3: Calendar navigation from voice
  calendarInitialDate,
  onBookingCancelled,
  // Phase D4.5.3: Voice-initiated cancellation
  pendingCancelBookingId,
  onPendingCancelHandled,
  // Voice-initiated selection
  pendingSelectBookingId,
  onPendingSelectHandled,
  // Phase D4.5.3: Voice confirmation of cancellation
  voiceConfirmCancel,
  voiceKeepBooking,
  onVoiceActionHandled,
  onCancelConfirmDismissed,
  // Phase D4.5.4: Modify booking props
  pendingModifyBookingId,
  pendingModifyChanges,
  onPendingModifyHandled,
  voiceConfirmModify,
  voiceCancelModify,
  onModifyVoiceActionHandled,
  onModifyConfirmDismissed,
  onBookingModified,
  // Voice booking list navigation
  voiceBookingNavTrigger,
  onVoiceBookingNavHandled,
  voiceDayNavTrigger,
  onVoiceDayNavHandled,
  // Phase P2.2-9: Callback to register the requests voice command handler
  onRequestsVoiceCommandReady,
  // Phase P2.2-10: Callback to register the green stay voice command handler
  onGreenStayVoiceCommandReady,
  onGridLeftBoundary,
  onMessagesVoiceCommandReady,
  onGridRightBoundary,
}: ConciergeGridProps) {
  const tv = useTVMotion();

  // Phase D3.3: Global voice hints state
  const [voiceHints, setVoiceHints] = useState<VoiceHintConfig>(DEFAULT_VOICE_HINTS);

  // Phase P2.2-9: Ref to MyRequestsView for voice command handling
  const myRequestsViewRef = useRef<MyRequestsViewRef>(null);

  // Phase P2.2-9: Create a handler that delegates to the MyRequestsView ref
  const handleRequestsVoiceCommand = useCallback((command: {
    type: 'NEXT_COLUMN' | 'PREV_COLUMN' | 'GO_TO_COLUMN' | 'NEXT_REQUEST' | 'PREV_REQUEST' |
          'FIRST_REQUEST' | 'LAST_REQUEST' | 'GO_TO_INDEX' | 'EXPAND' | 'COLLAPSE' |
          'READ_REQUEST' | 'COUNT' | 'STATUS' | 'BACK';
    payload?: { column?: 'NEW' | 'IN_PROGRESS' | 'COMPLETED'; index?: number };
  }) => {
    if (myRequestsViewRef.current) {
      return myRequestsViewRef.current.handleVoiceCommand(command);
    }
    return undefined;
  }, []);

  // Phase P2.2-9: Call parent callback to register our handler
  useEffect(() => {
    if (onRequestsVoiceCommandReady) {
      onRequestsVoiceCommandReady(handleRequestsVoiceCommand);
    }
  }, [onRequestsVoiceCommandReady, handleRequestsVoiceCommand]);

  // Callback for child views to update voice hints
  const updateVoiceHints = useCallback((hints: VoiceHintConfig | null) => {
    setVoiceHints(hints || DEFAULT_VOICE_HINTS);
  }, []);

  // When a view is open (weather/messages/calendar), it should be interactive
  // when in main zone OR sidebar zone, but NOT when in TV zone
  const viewsAreActive = enabled || (viewIsOpen && activeZone === 'sidebar');
  const isGridView = activeView === 'grid' && !selectedService;

  const lastFocusedIndexRef = useRef(0);
  const { isFocused, setFocusedIndex } = useKeyboardNavigation({
    gridColumns: 4,
    itemCount: mockServiceCards.length,
    enabled: enabled && isGridView, // Disable grid nav when any view is shown
    onSelect: (index) => {
      lastFocusedIndexRef.current = index; // P2.8-4b: save for restoration on return
      const service = mockServiceCards[index];
      if (service) {
        onServiceSelect?.(service.id);
      }
    },
    onLeftBoundary: onGridLeftBoundary,
    onRightBoundary: onGridRightBoundary,
  });

  // P2.8-4b: Restore last focused card when returning to grid (don't reset to index 0)
  useEffect(() => {
    if (isGridView) {
      setFocusedIndex(lastFocusedIndexRef.current);
    }
  }, [isGridView, setFocusedIndex]);

  // Reset to default hints when returning to grid view
  useEffect(() => {
    if (isGridView) {
      setVoiceHints(DEFAULT_VOICE_HINTS);
    }
  }, [isGridView]);

  // Determine current view title for header
  const getViewTitle = (): { icon: ReactNode; title: string } | null => {
    if (activeView === 'weather')    return { icon: <CloudSun       className="w-6 h-6 text-white/70" />, title: 'Weather' };
    if (activeView === 'messages')   return { icon: <MessageSquare  className="w-6 h-6 text-white/70" />, title: 'Messages' };
    if (activeView === 'calendar')   return { icon: <CalendarDays   className="w-6 h-6 text-white/70" />, title: 'My Calendar' };
    if (activeView === 'explore')    return { icon: <Compass        className="w-6 h-6 text-white/70" />, title: 'Explore Nearby' };
    if (activeView === 'requests')   return { icon: <Wrench         className="w-6 h-6 text-white/70" />, title: 'My Room' };
    if (activeView === 'green-stay') return { icon: <Leaf           className="w-6 h-6 text-white/70" />, title: 'Green Stay' };
    if (selectedService) return {
      icon: SERVICE_ICON_MAP[selectedService.id] ?? <Sparkles className="w-6 h-6 text-white/70" />,
      title: selectedService.title,
    };
    return null;
  };

  const viewTitle = getViewTitle();

  // Render content based on active view
  const renderContent = () => {
    // Weather view
    if (activeView === 'weather' && onViewClose) {
      return (
        <WeatherPlaceholderView
          key="weather"
          isActive={viewsAreActive}
          onClose={onViewClose}
        />
      );
    }

    // Messages view
    if (activeView === 'messages' && onViewClose) {
      return (
        <MessagesPlaceholderView
          key="messages"
          isActive={viewsAreActive}
          onClose={onViewClose}
          onMessagesVoiceCommandReady={onMessagesVoiceCommandReady}
        />
      );
    }

    // Phase D4.5.3: Stay-bounded calendar timeline
    if (activeView === 'calendar' && onViewClose && onSelectBooking && onSelectEvent && stayCheckIn && stayCheckOut) {
      return (
        <StayTimelineCalendar
          key="calendar"
          isActive={viewsAreActive}
          onClose={onViewClose}
          onSelectBooking={onSelectBooking}
          onSelectEvent={onSelectEvent}
          bookings={bookings}
          calendarEvents={calendarEvents}
          skipCleaningDays={skipCleaningDays}
          towelsOnlyDays={towelsOnlyDays}
          stayCheckIn={stayCheckIn}
          stayCheckOut={stayCheckOut}
          initialDate={calendarInitialDate}
          onBookingCancelled={onBookingCancelled}
          pendingCancelBookingId={pendingCancelBookingId}
          onPendingCancelHandled={onPendingCancelHandled}
          pendingSelectBookingId={pendingSelectBookingId}
          onPendingSelectHandled={onPendingSelectHandled}
          voiceConfirmCancel={voiceConfirmCancel}
          voiceKeepBooking={voiceKeepBooking}
          onVoiceActionHandled={onVoiceActionHandled}
          onCancelConfirmDismissed={onCancelConfirmDismissed}
          // Phase D4.5.4: Modify booking props
          pendingModifyBookingId={pendingModifyBookingId}
          pendingModifyChanges={pendingModifyChanges}
          onPendingModifyHandled={onPendingModifyHandled}
          voiceConfirmModify={voiceConfirmModify}
          voiceCancelModify={voiceCancelModify}
          onModifyVoiceActionHandled={onModifyVoiceActionHandled}
          onModifyConfirmDismissed={onModifyConfirmDismissed}
          onBookingModified={onBookingModified}
          voiceBookingNavTrigger={voiceBookingNavTrigger}
          onVoiceBookingNavHandled={onVoiceBookingNavHandled}
          voiceDayNavTrigger={voiceDayNavTrigger}
          onVoiceDayNavHandled={onVoiceDayNavHandled}
        />
      );
    }

    // Phase D3.1: Explore view (D3.3: with booking handler and voice hints)
    if (activeView === 'explore' && onViewClose) {
      return (
        <ExploreView
          key="explore"
          isActive={viewsAreActive}
          onClose={onViewClose}
          onBookingCreated={onVoiceBookingCreated}
          onVoiceHintsChange={updateVoiceHints}
        />
      );
    }

    // Phase P2.2-9: My Requests view
    if (activeView === 'requests' && onViewClose) {
      return (
        <MyRequestsView key="requests" ref={myRequestsViewRef} onClose={onViewClose} />
      );
    }

    // Phase P2.2-10: Green Stay view
    if (activeView === 'green-stay' && onViewClose && greenStayEnabled !== undefined && onGreenStayEnable && onGreenStayDisable && skipCleaningDays && towelsOnlyDays && stayCheckIn && stayCheckOut && onSkipCleaningChange && onTowelsOnlyChange) {
      return (
        <GreenStayView
          key="green-stay"
          greenStayEnabled={greenStayEnabled}
          skipCleaningDays={skipCleaningDays}
          towelsOnlyDays={towelsOnlyDays}
          stayCheckIn={stayCheckIn}
          stayCheckOut={stayCheckOut}
          roomNumber={roomNumber || ''}
          stayId={stayId || ''}
          onGreenStayEnable={onGreenStayEnable}
          onGreenStayDisable={onGreenStayDisable}
          onSkipCleaningChange={onSkipCleaningChange}
          onTowelsOnlyChange={onTowelsOnlyChange}
          onBack={onViewClose}
          onVoiceCommandReady={onGreenStayVoiceCommandReady}
          isActive={activeView === 'green-stay'}
        />
      );
    }

    // Service detail view
    if (selectedService && onServiceBack && onOptionSelect) {
      // Phase D2.4: Use RoomServiceDetailPage for room service
      if (selectedService.category === ServiceCategory.ROOM_SERVICE && onVoiceBookingCreated) {
        return (
          <RoomServiceDetailPage
            key={selectedService.id}
            isActive={enabled}
            onBack={onServiceBack}
            onOrderComplete={onVoiceBookingCreated}
            onVoiceHintsChange={updateVoiceHints}
          />
        );
      }

      // Phase D2.5: Use SpaDetailPage for spa
      if (selectedService.category === ServiceCategory.SPA && onVoiceBookingCreated) {
        return (
          <SpaDetailPage
            key={selectedService.id}
            isActive={enabled}
            onBack={onServiceBack}
            onBookingComplete={onVoiceBookingCreated}
            onVoiceHintsChange={updateVoiceHints}
          />
        );
      }

      // Phase D4.5.2: Use RestaurantDetailPage for restaurant
      if (selectedService.category === ServiceCategory.RESTAURANT && onVoiceBookingCreated) {
        return (
          <RestaurantDetailPage
            key={selectedService.id}
            isActive={enabled}
            onBack={onServiceBack}
            onBookingComplete={onVoiceBookingCreated}
            onVoiceHintsChange={updateVoiceHints}
            initialSlots={restaurantInitialSlots}
            stayCheckIn={stayCheckIn}
            stayCheckOut={stayCheckOut}
          />
        );
      }

      // P2.7-X: Bar, Pool, Gym, Transport → StandardServiceDetailPage (Restaurant-pattern flow)
      const standardCategories = [
        ServiceCategory.BAR,
        ServiceCategory.ACTIVITY,
        ServiceCategory.TRANSPORT,
      ];
      if (standardCategories.includes(selectedService.category) && onVoiceBookingCreated) {
        return (
          <StandardServiceDetailPage
            key={selectedService.id}
            service={selectedService}
            isActive={enabled}
            onBack={onServiceBack}
            onBookingComplete={onVoiceBookingCreated}
            onVoiceHintsChange={updateVoiceHints}
            initialOptionId={serviceInitialOption}
          />
        );
      }

      // Fallback generic service detail page (HOTEL_INFO etc.)
      return (
        <ServiceDetailPage
          key={selectedService.id}
          service={selectedService}
          onBack={onServiceBack}
          onSelectOption={onOptionSelect}
          isActive={enabled}
          initialOptionId={serviceInitialOption}
        />
      );
    }

    // Default: Service grid
    return (
      <>
        {/* Service Grid — service-grid class enables TV density overrides (P2.8-3b) */}
        <div className="service-grid flex-1 grid grid-cols-4 grid-rows-2 gap-5 mt-3">
          {mockServiceCards.map((card, index) => (
            <motion.div
              key={card.id}
              initial={tv.isTV ? { opacity: 0 } : { opacity: 0, scale: 0.95, y: 10 }}
              animate={tv.isTV ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
              transition={tv.entryTransition({
                delay: 0.08 * index,
                duration: 0.5,
                ease: [0.16, 1, 0.3, 1]
              })}
            >
              <ModernServiceCard
                data={card}
                isFocused={enabled && isFocused(index)}
                onClick={() => onServiceSelect?.(card.id)}
              />
            </motion.div>
          ))}
        </div>

        {/* Spacer to maintain gap before footer */}
        <div className="concierge-service-spacer mt-9 h-4" />
      </>
    );
  };

  return (
    <div className="concierge-grid-root h-full flex flex-col p-8">
      {/* Header with Zone Indicator - Always visible */}
      <div className="concierge-grid-header flex items-start justify-between mb-8 flex-shrink-0">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          {viewTitle ? (
            // Show view title with back button when in a sub-view
            <div className="flex items-center gap-4">
              <motion.button
                onClick={() => {
                  if (selectedService && onServiceBack) {
                    onServiceBack();
                  } else if (onViewClose) {
                    onViewClose();
                  }
                }}
                className="w-11 h-11 rounded-full bg-white/[0.06] backdrop-blur-sm flex items-center justify-center text-white/80 hover:bg-white/[0.1] transition-all duration-300"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </motion.button>
              <div>
                <h1 className="font-display text-2xl font-semibold text-white/95 flex items-center gap-3 tracking-tight">
                  {viewTitle.icon}
                  {viewTitle.title}
                </h1>
                <p className="text-white/35 text-sm mt-1">
                  Press Esc to go back
                </p>
              </div>
            </div>
          ) : (
            // Default welcome header
            <>
              <h1 className="font-display text-3xl font-semibold text-white/95 mb-2 tracking-tight">
                Concierge
              </h1>
              <p className="text-white/40">
                How can we help you today?
              </p>
            </>
          )}
        </motion.div>
        <ZoneIndicator activeZone={activeZone} />
      </div>

      {/* Content Area - Changes based on view */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeView + (selectedService?.id || '')}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="flex-1 flex flex-col min-h-0 overflow-hidden"
        >
          {renderContent()}
        </motion.div>
      </AnimatePresence>

      {/* Spacer for FloatingVoiceBar */}
      <div className="concierge-grid-voice-spacer flex-shrink-0 h-[3.25rem]" />

      {/* Phase D3.3: Global Voice Hints Footer - Always visible */}
      <div className="concierge-grid-footer flex-shrink-0 px-6 py-3 border-t border-white/[0.06] bg-zinc-950/50">
        <div className="flex items-center justify-between">
          {/* Keyboard shortcuts */}
          <div className="flex items-center gap-4 text-xs text-white/30">
            <span>← → Navigate</span>
            <span>Enter Select</span>
            <span>Esc Back</span>
            <span className="flex items-center gap-1">
              <Mic className="w-3 h-3" />
              Hold Space
            </span>
          </div>

          {/* Voice hints - changes based on active view */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Mic className="w-4 h-4 text-amber-400/70" />
              <span className="text-xs text-white/40">{voiceHints.title}:</span>
            </div>
            <div className="flex items-center gap-2">
              {voiceHints.hints.map((hint) => (
                <span
                  key={hint}
                  className={`px-3 py-1 rounded-full text-xs ${
                    voiceHints.variant === 'action'
                      ? 'bg-amber-500/20 text-amber-300'
                      : voiceHints.variant === 'confirm'
                      ? 'bg-emerald-500/20 text-emerald-300'
                      : 'bg-white/10 text-white/60'
                  }`}
                >
                  "{hint}"
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
